import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { FreeDeliveryPill } from '../components/custom/AnnouncementBar';
import { ProductReviews } from '../components/custom/ProductReviews';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Star, Minus, Plus, ShoppingCart, Truck, Shield, RotateCcw, Eye, Clock, BadgeCheck, CreditCard, Share2, Flame, TrendingUp, Package, Zap } from 'lucide-react';
import api from '../utils/api';
import { trackPageView, trackAddToCart } from '../utils/analytics';
import { SEO } from '../components/SEO';

function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [viewersCount, setViewersCount] = useState(0);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Check if product is already in cart
  const cartItem = cart.find(item => item.id === id);
  const quantityInCart = cartItem?.quantity || 0;

  // Simulate viewers count for social proof
  useEffect(() => {
    const baseViewers = Math.floor(Math.random() * 5) + 2; // 2-6 viewers
    setViewersCount(baseViewers);
    
    // Fluctuate viewers every 10-20 seconds
    const interval = setInterval(() => {
      setViewersCount(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const newCount = prev + change;
        return Math.max(2, Math.min(8, newCount)); // Keep between 2-8
      });
    }, Math.random() * 10000 + 10000);

    return () => clearInterval(interval);
  }, [id]);

  // Desktop sticky CTA on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const data = await api.getProduct(id);
        setProduct(data);
        
        // Update page title and meta for SEO
        document.title = `${data.title} | Mothering`;
        
        // Add product structured data
        const structuredData = {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data.title,
          "description": data.description,
          "image": data.image_urls || [],
          "brand": {
            "@type": "Brand",
            "name": "Mothering"
          },
          "offers": {
            "@type": "Offer",
            "url": `https://mothering.in/product/${data.id}`,
            "priceCurrency": "INR",
            "price": data.price,
            "availability": data.in_stock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": "Mothering"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "INR"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "businessDays": {
                  "@type": "QuantitativeValue",
                  "minValue": 3,
                  "maxValue": 5
                }
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "IN"
              }
            }
          }
        };
        
        // Add aggregate rating if reviews exist
        if (data.review_count > 0) {
          structuredData.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": data.avg_rating,
            "reviewCount": data.review_count
          };
        }
        
        // Inject structured data script
        let script = document.getElementById('product-structured-data');
        if (!script) {
          script = document.createElement('script');
          script.id = 'product-structured-data';
          script.type = 'application/ld+json';
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(structuredData);
        
        // Track Facebook Pixel ViewContent event
        if (window.fbq) {
          window.fbq('track', 'ViewContent', {
            content_ids: [data.id],
            content_type: 'product',
            content_name: data.title,
            content_category: data.category,
            value: data.price,
            currency: 'INR',
          });
        }
        
        // Track Google Ads view_item event (if gtag exists)
        if (window.gtag) {
          window.gtag('event', 'view_item', {
            currency: 'INR',
            value: data.price,
            items: [{
              item_id: data.id,
              item_name: data.title,
              item_category: data.category,
              price: data.price,
              quantity: 1
            }]
          });
        }
        
        // Track in our analytics
        trackPageView('Product Detail', `/products/${data.id}`, data.id, data.title);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    toast.success(`${quantity > 1 ? quantity + 'x ' : ''}${product.title} added to cart!`);
    
    // Track add to cart in our analytics
    trackAddToCart(product.id, product.title, product.price * quantity);
    
    // Reset quantity after adding
    setQuantity(1);
  };

  const incrementQuantity = () => setQuantity(prev => Math.min(prev + 1, 10));
  const decrementQuantity = () => setQuantity(prev => Math.max(prev - 1, 1));

  // Share function - uses /product/ URL for proper OG tags
  const handleShare = async () => {
    const shareUrl = `https://mothering.in/product/${product.id}`;
    const shareData = {
      title: product.title,
      text: `Check out ${product.title} - ₹${product.price} at Mothering!`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      // User cancelled or error
      if (error.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          <Skeleton className="h-12 w-32 mb-6" />
          <div className="grid lg:grid-cols-2 gap-8">
            <Skeleton className="h-96 rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Calculate savings
  const savingsAmount = product.original_price ? product.original_price - product.price : 0;
  const savingsPercent = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  
  // Simulated sold count (in real app, fetch from DB)
  const soldCount = Math.floor(Math.random() * 300) + 200; // 200-500 sold
  
  // Calculate hours left for same-day dispatch (if before 2 PM)
  const now = new Date();
  const cutoffHour = 14; // 2 PM
  const hoursUntilCutoff = cutoffHour - now.getHours();
  const showSameDayDispatch = hoursUntilCutoff > 0 && now.getHours() >= 8;

  // Generate SEO values - ensure they are strings
  const pageTitle = `${product.title || 'Product'} | Buy Online | Mothering`;
  const seoDescription = `Buy ${product.title || 'Product'} online at Mothering. ${(product.description || '').substring(0, 120)}... Free delivery across India!`;
  const productUrl = `https://mothering.in/products/${product.id}`;
  const productImage = product.image_urls?.[0] || 'https://mothering.in/logo512.png';
  const ogTitle = `${product.title || 'Product'} - Mothering`;
  const priceString = String(product.price || 0);

  return (
    <>
      {/* Dynamic SEO with Rich Product Schema */}
      <SEO 
        title={pageTitle}
        description={seoDescription}
        type="product"
        image={productImage}
        url={`/product/${product.id}`}
        keywords={`${product.title}, ${product.category_name || product.category}, kids products, buy online india`}
        product={{
          price: product.price,
          currency: 'INR',
          availability: product.in_stock ? 'in stock' : 'out of stock',
          condition: 'new',
          brand: 'Mothering',
          rating: product.rating ? {
            value: product.rating.average || 4.5,
            count: product.rating.count || 0
          } : null
        }}
      />

    <div className="min-h-screen pb-20" data-testid="product-detail-page">
      <div className="container mx-auto max-w-6xl px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Product Gallery */}
          <div className="lg:col-span-6" data-testid="product-gallery">
            <Carousel className="w-full">
              <CarouselContent>
                {product.image_urls?.map((url, index) => (
                  <CarouselItem key={index}>
                    <AspectRatio ratio={1}>
                      <img
                        src={url}
                        alt={`${product.title} ${index + 1}`}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </AspectRatio>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {product.image_urls?.length > 1 && (
                <>
                  <CarouselPrevious data-testid="product-gallery-prev" />
                  <CarouselNext data-testid="product-gallery-next" />
                </>
              )}
            </Carousel>
          </div>

          {/* Product Info */}
          <div className="lg:col-span-6 space-y-4">
            {/* Badges Row */}
            <div className="flex flex-wrap items-center gap-2">
              <FreeDeliveryPill />
              <Badge variant="outline">
                {product.category === 'lunchbox' ? '🍱 Lunchbox' : '🎒 Bag'}
              </Badge>
              {/* Bestseller Badge */}
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
                <Flame className="w-3 h-3 mr-1" />
                Bestseller
              </Badge>
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{product.title}</h1>
            
            {/* Rating & Sold Count - PROMINENT */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Star Rating */}
              <div className="flex items-center gap-1.5 bg-green-50 px-2.5 py-1 rounded-full">
                <Star className="w-4 h-4 fill-green-600 text-green-600" />
                <span className="font-bold text-green-700">{product.avg_rating?.toFixed(1) || '4.8'}</span>
                <span className="text-sm text-green-600">({product.review_count || 127} reviews)</span>
              </div>
              
              {/* Sold Count */}
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <TrendingUp className="w-4 h-4 text-orange-500" />
                <span className="font-semibold">{soldCount}+ sold</span>
              </div>
            </div>

            {/* Price Section - Enhanced */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-3xl font-extrabold text-gray-900" data-testid="product-price">
                  ₹{product.price}
                </span>
                {product.original_price && (
                  <span className="text-lg text-gray-400 line-through">
                    ₹{product.original_price}
                  </span>
                )}
              </div>
              {savingsAmount > 0 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600 text-white text-sm px-2 py-0.5">
                    You Save ₹{savingsAmount} ({savingsPercent}% OFF)
                  </Badge>
                </div>
              )}
            </div>

            {/* Urgency Section */}
            <div className="flex flex-wrap gap-2">
              {/* Live viewers */}
              <div className="flex items-center gap-1.5 text-sm bg-red-50 text-red-700 px-3 py-1.5 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <Eye className="w-4 h-4" />
                <span><span className="font-bold">{viewersCount}</span> viewing now</span>
              </div>

              {/* Low stock warning */}
              {product.stock && product.stock <= 10 && product.stock > 0 && (
                <div className="flex items-center gap-1.5 text-sm bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full font-medium">
                  <Package className="w-4 h-4" />
                  Only {product.stock} left!
                </div>
              )}

              {/* Same day dispatch */}
              {showSameDayDispatch && (
                <div className="flex items-center gap-1.5 text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                  <Zap className="w-4 h-4" />
                  <span>Order in <span className="font-bold">{hoursUntilCutoff}h</span> for same-day dispatch</span>
                </div>
              )}
            </div>

            {/* Quick Review Snippet - Social Proof (Category-appropriate) */}
            {(() => {
              // Use actual review if available, otherwise show category-appropriate placeholder
              const actualReview = product.reviews?.[0];
              
              // Category-appropriate placeholder reviews
              const placeholderReviews = {
                lunchbox: {
                  name: "Priya M.",
                  initial: "P",
                  text: "My daughter loves it! Keeps food warm till lunch. Best purchase this year!",
                  gradient: "from-pink-400 to-purple-400"
                },
                bag: {
                  name: "Sneha R.",
                  initial: "S",
                  text: "Perfect size for my son's school! Durable, comfortable straps, and he loves the design!",
                  gradient: "from-blue-400 to-cyan-400"
                }
              };
              
              const review = actualReview || placeholderReviews[product.category] || placeholderReviews.lunchbox;
              
              return (
                <div className="bg-gray-50 rounded-xl p-4 border">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${actualReview ? 'from-green-400 to-teal-400' : review.gradient} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                      {actualReview ? actualReview.name?.charAt(0).toUpperCase() : review.initial}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{actualReview?.name || review.name}</span>
                        <div className="flex">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`w-3 h-3 ${i <= (actualReview?.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                        <Badge variant="outline" className="text-xs py-0 px-1.5 text-green-600 border-green-200">
                          Verified
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 italic">
                        "{actualReview?.comment || review.text}"
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Share Button - Moved here */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={handleShare}
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            </div>

            {/* Short Description */}
            <p className="text-gray-600 text-sm">{product.description}</p>

            {/* Trust Badges - Compact */}
            <div className="grid grid-cols-4 gap-2">
              <div className="flex flex-col items-center text-center p-2 bg-gray-50 rounded-lg">
                <BadgeCheck className="w-5 h-5 text-green-600 mb-1" />
                <span className="text-[10px] text-gray-600 leading-tight">1000+ Happy Parents</span>
              </div>
              <div className="flex flex-col items-center text-center p-2 bg-gray-50 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600 mb-1" />
                <span className="text-[10px] text-gray-600 leading-tight">BPA-Free Safe</span>
              </div>
              <div className="flex flex-col items-center text-center p-2 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-orange-600 mb-1" />
                <span className="text-[10px] text-gray-600 leading-tight">Free Delivery</span>
              </div>
              <div className="flex flex-col items-center text-center p-2 bg-gray-50 rounded-lg">
                <RotateCcw className="w-5 h-5 text-purple-600 mb-1" />
                <span className="text-[10px] text-gray-600 leading-tight">Easy Returns</span>
              </div>
            </div>

            {/* Colors */}
            {product.colors?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-sm">Available Colors:</h3>
                <div className="flex gap-2 flex-wrap">
                  {product.colors.map(color => (
                    <Badge key={color} variant="secondary">{color}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Features - Compact */}
            {product.features?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-sm">Key Features:</h3>
                <div className="flex flex-wrap gap-2">
                  {product.features.slice(0, 4).map((feature, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      ✓ {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selector & Add to Cart - Desktop */}
            <div className="hidden lg:block space-y-3 pt-2">
              {/* Quantity Selector */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">Quantity:</span>
                <div className="flex items-center border rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="h-10 w-10 rounded-r-none"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-12 text-center font-semibold" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={incrementQuantity}
                    disabled={quantity >= 10}
                    className="h-10 w-10 rounded-l-none"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {quantityInCart > 0 && (
                  <span className="text-sm text-muted-foreground">
                    ({quantityInCart} in cart)
                  </span>
                )}
              </div>

              {/* Add to Cart Button - More Prominent */}
              <Button
                size="lg"
                className="w-full rounded-xl h-14 text-lg bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-lg"
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {product.in_stock ? `Add to Cart - ₹${(product.price * quantity).toLocaleString()}` : 'Out of Stock'}
              </Button>
            </div>

            {/* Details Accordion */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="details">
                <AccordionTrigger>Product Details</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 text-sm">
                    <p><strong>Age Range:</strong> {product.age_range} years</p>
                    {product.materials?.length > 0 && (
                      <p><strong>Materials:</strong> {product.materials.join(', ')}</p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping">
                <AccordionTrigger>Shipping & Delivery</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm">
                    <strong>Free delivery</strong> on all orders. Same-day dispatch for orders placed before 2 PM.
                    Delivered within 3-5 business days across India.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="container mx-auto max-w-6xl px-4 pb-32 lg:pb-8">
        <ProductReviews productId={id} />
      </div>

      {/* Sticky Bottom Bar - Mobile */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 bg-white border-t p-3 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          {/* Price */}
          <div className="flex-shrink-0">
            <p className="text-xs text-muted-foreground">Price</p>
            <p className="text-lg font-extrabold">₹{(product.price * quantity).toLocaleString()}</p>
          </div>
          
          {/* Quantity Selector - Mobile */}
          <div className="flex items-center border rounded-lg flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={decrementQuantity}
              disabled={quantity <= 1}
              className="h-9 w-9 p-0 rounded-r-none"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="w-8 text-center font-semibold text-sm">{quantity}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={incrementQuantity}
              disabled={quantity >= 10}
              className="h-9 w-9 p-0 rounded-l-none"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Add to Cart Button */}
          <Button
            size="lg"
            className="flex-1 rounded-xl h-11 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
            onClick={handleAddToCart}
            disabled={!product.in_stock}
            data-testid="add-to-cart-button-mobile"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
          </Button>
        </div>
      </div>

      {/* Desktop Sticky CTA Bar - Shows on scroll */}
      {showStickyBar && (
        <div className="hidden lg:block fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-30 animate-in slide-in-from-bottom duration-300">
          <div className="container mx-auto max-w-6xl px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Product Info */}
              <div className="flex items-center gap-4">
                <img 
                  src={product.image_urls?.[0]} 
                  alt={product.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <p className="font-semibold line-clamp-1">{product.title}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">₹{product.price}</span>
                    {product.original_price && (
                      <span className="text-sm text-muted-foreground line-through">₹{product.original_price}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="h-10 w-10 rounded-r-none"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-10 text-center font-semibold">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={incrementQuantity}
                    disabled={quantity >= 10}
                    className="h-10 w-10 rounded-l-none"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  size="lg"
                  className="rounded-xl px-8 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
                  onClick={handleAddToCart}
                  disabled={!product.in_stock}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart - ₹{(product.price * quantity).toLocaleString()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default ProductDetailPage;